// src/components/module/cotizaciones/CotizacionDetailModal.tsx
import React, { useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Grid,
  IconButton,
  Divider,
  Avatar,
  Stack,
  Fade,
  Alert,
  AlertTitle,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DiscountIcon from '@mui/icons-material/Discount';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import { formatMoney, formatDate } from '../../../utils/format';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { CotizacionPDF } from './documentos/CotizacionPDF';
import { DarkModeContext } from "../../../context/darkModeContext";
import { DetalleCotizacion } from './interfaces/cotizacion.interfaces';
import { ArticuloCotizacion } from './interfaces/cotizacion.interfaces';

// Tema profesional
interface CotizacionDetailModalProps {
  cotizacion: DetalleCotizacion | null;
  open: boolean;
  onClose: () => void;
}

const getEstadoConfig = (estado: string) => {
  switch (estado.toLowerCase()) {
    case 'finalizada':
      return {
        color: 'success' as const,
        icon: <CheckCircleIcon />,
      };
    case 'abierta':
      return {
        color: 'warning' as const,
        icon: <PendingIcon />,
      };
    default:
      return {
        color: 'default' as const,
        icon: <RequestQuoteIcon />,
      };
  }
};

export const CotizacionDetailModal: React.FC<CotizacionDetailModalProps> = ({
  cotizacion,
  open,
  onClose
}) => {
  const { darkMode } = useContext(DarkModeContext);

  if (!cotizacion) return null;

  const estadoConfig = getEstadoConfig(cotizacion.estado);

  return (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        {/* Header mejorado */}
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
            color: 'white',
            p: 3,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  width: 48,
                  height: 48,
                }}
              >
                <RequestQuoteIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Cotización N° {cotizacion.codigoCotizacion}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {formatDate(cotizacion.fecha)}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                icon={estadoConfig.icon}
                label={cotizacion.estado}
                color={estadoConfig.color}
                sx={{
                  fontWeight: 700,
                  height: 32,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '& .MuiChip-icon': {
                    color: 'white',
                  }
                }}
              />
              <IconButton
                title="Descargar cotización"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                  }
                }}
              >
                <PDFDownloadLink
                  document={<CotizacionPDF
                    cotizacion={cotizacion}
                    configuracionEmpresa={{
                      nombre: '',
                      nit: '',
                      direccion: '',
                      telefono: '',
                      email: '',
                      sitioWeb: undefined
                    }}
                  />}
                  fileName={`cotizacion-${cotizacion.codigoCotizacion}.pdf`}
                  style={{ color: 'inherit', display: 'flex' }}
                >
                  {({ loading }) =>
                    loading ? 'Cargando...' : <DownloadIcon />
                  }
                </PDFDownloadLink>
              </IconButton>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                  }
                }}
                title="Cerrar"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Fade in timeout={300}>
            <Grid container spacing={3}>
              {/* Información General */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                  }}
                >
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    Información General
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                        <CalendarTodayIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Fecha
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(cotizacion.fecha)}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                        <StoreIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Sucursal
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {cotizacion.sucursal.nombre}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                        <BadgeIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Vendedor
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {cotizacion.vendedor.nombre}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              {/* Información del Cliente */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                  }}
                >
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    Información del Cliente
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Nombre
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {cotizacion.cliente.nombre}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <BadgeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Identificación
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {cotizacion.cliente.identificacion}
                      </Typography>
                    </Box>

                    {cotizacion.cliente.direccion && (
                      <>
                        <Divider />
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <HomeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                              Dirección
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            {cotizacion.cliente.direccion}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {cotizacion.cliente.telefono && (
                      <>
                        <Divider />
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                              Teléfono
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            {cotizacion.cliente.telefono}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              {/* Detalle de Artículos */}
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <ShoppingCartIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight={700}>
                      Artículos Cotizados ({cotizacion.articulos.length})
                    </Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: darkMode ? 'rgba(8, 145, 178, 0.1)' : 'rgba(8, 145, 178, 0.05)' }}>
                            Código
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: darkMode ? 'rgba(8, 145, 178, 0.1)' : 'rgba(8, 145, 178, 0.05)' }}>
                            Artículo
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: darkMode ? 'rgba(8, 145, 178, 0.1)' : 'rgba(8, 145, 178, 0.05)' }}>
                            Cantidad
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: darkMode ? 'rgba(8, 145, 178, 0.1)' : 'rgba(8, 145, 178, 0.05)' }}>
                            Precio Unit.
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: darkMode ? 'rgba(8, 145, 178, 0.1)' : 'rgba(8, 145, 178, 0.05)' }}>
                            Descuento
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: darkMode ? 'rgba(8, 145, 178, 0.1)' : 'rgba(8, 145, 178, 0.05)' }}>
                            Subtotal
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cotizacion.articulos.map((articulo: ArticuloCotizacion) => {
                          const precioVenta = articulo.PrecioVenta || 0;
                          const cantidad = articulo.Cantidad || 1;
                          const descuento = articulo.Descuento || 0;

                          const subtotalSinDescuento = precioVenta * cantidad;
                          const descuentoMonto = subtotalSinDescuento * (descuento / 100);
                          const subtotalConDescuento = subtotalSinDescuento - descuentoMonto;

                          return (
                            <TableRow
                              key={articulo.Id || Math.random()}
                              sx={{
                                '&:hover': {
                                  bgcolor: darkMode ? 'rgba(8, 145, 178, 0.05)' : 'rgba(8, 145, 178, 0.02)',
                                }
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={articulo.EAN || "-"}
                                  size="small"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {articulo.Nombre}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={cantidad}
                                  size="small"
                                  color="primary"
                                  sx={{ fontWeight: 700, minWidth: 50 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600}>
                                  {formatMoney(precioVenta)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {descuento > 0 ? (
                                  <Chip
                                    label={`${descuento}%`}
                                    size="small"
                                    color="error"
                                    icon={<DiscountIcon />}
                                    sx={{ fontWeight: 700 }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Box>
                                  <Typography variant="body2" fontWeight={700} color="success.main">
                                    {formatMoney(subtotalConDescuento)}
                                  </Typography>
                                  {descuento > 0 && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ textDecoration: "line-through" }}
                                    >
                                      {formatMoney(subtotalSinDescuento)}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* Totales */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      minWidth: 350,
                      border: '2px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                      Resumen de Cotización
                    </Typography>

                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalOfferIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight={600}>
                            Subtotal
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={600}>
                          {formatMoney(cotizacion.subtotal)}
                        </Typography>
                      </Box>

                      {cotizacion.descuentoTotal > 0 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DiscountIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            <Typography variant="body2" fontWeight={600} color="error.main">
                              Descuento total
                            </Typography>
                          </Box>
                          <Typography variant="body1" fontWeight={600} color="error.main">
                            - {formatMoney(cotizacion.descuentoTotal)}
                          </Typography>
                        </Box>
                      )}

                      {cotizacion.impuestos.map((impuesto, index) => (
                        <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {impuesto.nombre}
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {formatMoney(impuesto.valor)}
                          </Typography>
                        </Box>
                      ))}

                      <Divider />

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background: darkMode
                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                        }}
                      >
                        <Typography variant="h6" fontWeight={700} textTransform="uppercase">
                          Total
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color="success.main">
                          {formatMoney(cotizacion.totalAPagar)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Box>
              </Grid>

              {/* Observaciones */}
              {cotizacion.observaciones && (
                <Grid item xs={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                    }}
                  >
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <AlertTitle sx={{ fontWeight: 700 }}>Observaciones</AlertTitle>
                      {cotizacion.observaciones}
                    </Alert>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Fade>
        </DialogContent>
      </Dialog>
  );
};